import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FiCheck, FiFeather, FiGlobe, FiHeart, FiRefreshCw, FiTruck, FiX } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/productdetails.scss";

function ProductDetail() {
  const { id } = useParams();
  const product = productData.find((item) => item.id === Number(id));
  const { addToCart, toggleWishlist, isInWishlist } = useContext(CartContext);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState(product?.colors[0] || "");
  const [sizeError, setSizeError] = useState(false);
  const [added, setAdded] = useState(false);
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
  const sizeGuideTriggerRef = useRef(null);
  const sizeGuidePanelRef = useRef(null);
  const sizeGuideCloseRef = useRef(null);

  useEffect(() => {
    setSelectedSize("");
    setSelectedColor(product?.colors[0] || "");
    setAdded(false);
    setSizeGuideOpen(false);
  }, [product]);

  useEffect(() => {
    if (!sizeGuideOpen) return undefined;

    const panel = sizeGuidePanelRef.current;
    const trigger = sizeGuideTriggerRef.current;
    const focusTimer = window.setTimeout(() => sizeGuideCloseRef.current?.focus(), 0);
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setSizeGuideOpen(false);
        return;
      }
      if (event.key !== "Tab" || !panel) return;
      const focusable = [...panel.querySelectorAll('button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])')];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.body.classList.add("nav-open");
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.body.classList.remove("nav-open");
      document.removeEventListener("keydown", handleKeyDown);
      trigger?.focus();
    };
  }, [sizeGuideOpen]);

  const relatedProducts = useMemo(() => {
    if (!product) return [];
    const sameCategory = productData.filter((item) => item.id !== product.id && item.category === product.category);
    const sameGender = productData.filter((item) => item.id !== product.id && item.gender === product.gender && !sameCategory.includes(item));
    return [...sameCategory, ...sameGender].slice(0, 4);
  }, [product]);

  if (!product) {
    return (
      <main className="product-not-found">
        <span>404</span>
        <h1>This piece has sailed on.</h1>
        <p>It may be sold out or the link may have changed.</p>
        <Link className="button-primary" to="/shop">Return to shop</Link>
      </main>
    );
  }

  const selectedColorHex = product.colorOptions.find((option) => option.name === selectedColor)?.hex || product.colorHex;

  const handleAddToCart = () => {
    if (!selectedSize) {
      setSizeError(true);
      return;
    }
    addToCart(product.id, 1, selectedSize, selectedColor);
    setSizeError(false);
    setAdded(true);
  };

  return (
    <main className="product-detail">
      <nav className="product-breadcrumb" aria-label="Breadcrumb">
        <Link to="/shop">Shop</Link><span>/</span><Link to={`/shop?category=${encodeURIComponent(product.category)}`}>{product.category}</Link><span>/</span><span>{product.name}</span>
      </nav>

      <section className="product-detail__top">
        <div className="product-gallery">
          <figure className="product-gallery__primary">
            <img src={product.images[0]} alt={`${product.name} — ${product.colorName}`} />
            <figcaption>{product.attributes.model}</figcaption>
          </figure>
          <figure className="product-gallery__detail" aria-hidden="true">
            <img src={product.images[0]} alt="" />
          </figure>
        </div>

        <aside className="product-purchase">
          <div className="product-purchase__badges">{product.badges.map((badge) => <span key={badge}>{badge}</span>)}</div>
          <p className="eyebrow">{product.category}</p>
          <h1>{product.name} <span>— {selectedColor}</span></h1>
          <div className="product-purchase__price"><strong>{formatPrice(product.price)}</strong>{product.compareAt && <del>{formatPrice(product.compareAt)}</del>}<span>{product.status}</span></div>
          <p className="product-purchase__description">{product.description}</p>

          <div className="product-option">
            <div className="product-option__heading"><span>Colour</span><strong>{selectedColor}</strong></div>
            <div className="product-colours">
              {product.colorOptions.map((option) => <button key={option.name} type="button" className={selectedColor === option.name ? "is-active" : ""} onClick={() => { setSelectedColor(option.name); setAdded(false); }} aria-label={`Choose ${option.name}`}><i style={{ background: option.hex }} /><span>{option.name}</span></button>)}
            </div>
          </div>

          <div className={`product-option ${sizeError ? "has-error" : ""}`}>
            <div className="product-option__heading"><span>Size</span><button ref={sizeGuideTriggerRef} type="button" onClick={() => setSizeGuideOpen(true)} aria-haspopup="dialog">Size guide</button></div>
            <div className="product-sizes">
              {product.sizes.map((size) => <button key={size} type="button" className={selectedSize === size ? "is-active" : ""} onClick={() => { setSelectedSize(size); setSizeError(false); setAdded(false); }}>{size}</button>)}
            </div>
            {sizeError && <p role="alert">Please select a size.</p>}
          </div>

          <div className="product-actions">
            <button className="button-primary" type="button" onClick={handleAddToCart}>{added ? <><FiCheck /> Added to bag</> : "Add to bag"}</button>
            <button type="button" className={isInWishlist(product.id) ? "is-active" : ""} onClick={() => toggleWishlist(product.id)} aria-label={`${isInWishlist(product.id) ? "Remove from" : "Add to"} wishlist`}><FiHeart /></button>
          </div>
          {added && <Link className="product-view-bag" to="/cartpage">View shopping bag →</Link>}

          {product.status === "Pre-order" && <div className="product-dispatch"><span>i</span><p><strong>Estimated dispatch: {siteConfig.commerce.preOrderDispatch}</strong><br />Your whole order ships when every piece is ready.</p></div>}

          <div className="product-accordions">
            <details open>
              <summary>Details <span>+</span></summary>
              <div><p>{product.attributes.origin}</p><ul>{product.features.map((feature) => <li key={feature}>{feature}</li>)}</ul><p>{product.attributes.material}</p></div>
            </details>
            <details>
              <summary>Delivery <span>+</span></summary>
              <div><p>USA: 3–10 working days after dispatch.</p><p>Delivery is {formatPrice(siteConfig.commerce.standardShipping)}, or free over {formatPrice(siteConfig.commerce.shippingThreshold)}.</p></div>
            </details>
            <details>
              <summary>Returns <span>+</span></summary>
              <div><p>Return unworn pieces with their tags within {siteConfig.commerce.returnWindow} days of delivery.</p><Link to="/returns">Read the full return policy →</Link></div>
            </details>
            <details>
              <summary>Care <span>+</span></summary>
              <div><p>{product.attributes.care}. Air between wears and wash only when needed to help your garment last.</p></div>
            </details>
          </div>
        </aside>
      </section>

      <section className="product-promises" aria-label="Product assurances">
        <div><FiFeather /><span><strong>Considered fibres</strong><small>Lower-impact material choices</small></span></div>
        <div><FiGlobe /><span><strong>Made in Europe</strong><small>With trusted makers</small></span></div>
        <div><FiTruck /><span><strong>Free shipping</strong><small>On orders over {formatPrice(siteConfig.commerce.shippingThreshold)}</small></span></div>
        <div><FiRefreshCw /><span><strong>{siteConfig.commerce.returnWindow}-day returns</strong><small>Try it at home</small></span></div>
      </section>

      <section className="product-material-story">
        <div className="product-material-story__visual" style={{ backgroundColor: selectedColorHex }}><span>{siteConfig.logo.symbol}</span><p>{product.attributes.material}</p></div>
        <div className="product-material-story__copy">
          <p className="eyebrow">Sustainability & materials</p>
          <h2>Made to feel good.<br />Made to stay.</h2>
          <p>We select fibres for comfort, durability and a lighter footprint. {product.attributes.material} gives this piece its distinctive hand feel while careful construction helps it keep its shape, season after season.</p>
          <Link className="text-link" to="/about">How we make our clothes</Link>
        </div>
      </section>

      <section className="product-related page-shell">
        <div><p className="eyebrow">You may also like</p><h2>Continue exploring</h2></div>
        <div className="product-related__grid">
          {relatedProducts.map((item) => <article key={item.id}><Link to={`/shopDetails/${item.id}`}><img src={item.image} alt={`${item.name} — ${item.colorName}`} loading="lazy" /><span><strong>{item.name}</strong><small>{item.colorName}</small><em>{formatPrice(item.price)}</em></span></Link></article>)}
        </div>
      </section>

      {sizeGuideOpen && (
        <div className="size-guide" role="dialog" aria-modal="true" aria-labelledby="size-guide-title">
          <button className="size-guide__backdrop" type="button" onClick={() => setSizeGuideOpen(false)} aria-label="Close size guide" />
          <div className="size-guide__panel" ref={sizeGuidePanelRef}>
            <button ref={sizeGuideCloseRef} type="button" onClick={() => setSizeGuideOpen(false)} aria-label="Close size guide"><FiX /></button>
            <p className="eyebrow">Find your fit</p>
            <h2 id="size-guide-title">Size guide</h2>
            <p>Measurements refer to your body, in centimetres. If you are between sizes, choose the larger size for a relaxed fit.</p>
            <table><thead><tr><th>Size</th><th>Chest</th><th>Waist</th><th>Hip</th></tr></thead><tbody>{product.sizes.map((size, index) => <tr key={size}><th>{size}</th><td>{80 + index * 4}–{84 + index * 4}</td><td>{64 + index * 4}–{68 + index * 4}</td><td>{89 + index * 4}–{93 + index * 4}</td></tr>)}</tbody></table>
          </div>
        </div>
      )}
    </main>
  );
}

export default ProductDetail;
